<template>
    <v-form ref="ccInfoForm">
        <v-layout class="mt-2 mb-3" align-center justify-space-between>
            <v-layout column>
                <div class="text-h6 mb-2">Credit Card Information</div>
                <div class="text-caption">All fields required.</div>
            </v-layout>
            <v-flex>
                <v-checkbox
                    data-testid="fill-cc-form-checkbox"
                    class="fill-cc-form-checkbox"
                    v-model="creditCard.useExistingInfo"
                    @change="fillForm"
                    :disabled="!recording"
                    label="Use customer info on file"
                />
            </v-flex>
        </v-layout>
        <v-layout>
            <app-input
                data-testid="cc-first-name-input"
                class="cc-first-name-input"
                label="First Name"
                v-model="firstName"
                :rules="'required'"
                :disabled="!recording"
            ></app-input>
            <div class="px-2"></div>
            <app-input
                data-testid="cc-last-name-input"
                class="cc-last-name-input"
                label="Last Name"
                v-model="lastName"
                :rules="'required'"
                :disabled="!recording"
            ></app-input>
        </v-layout>
        <v-layout>
            <v-flex xs6>
                <app-input
                    data-testid="cc-billing-address-input"
                    class="cc-billing-address-input mr-2"
                    label="Billing Address"
                    v-model="billingAddress"
                    :rules="'required'"
                    :disabled="!recording"
                ></app-input>
            </v-flex>
            <v-flex xs3>
                <v-select
                    data-testid="cc-billing-state-menu"
                    class="cc-billing-state-menu mx-2"
                    label="Billing State"
                    :items="states"
                    v-model="billingState"
                    :rules="[(v) => !!v || 'This field is required']"
                    :disabled="!recording"
                    item-text="value"
                />
            </v-flex>
            <v-flex xs3>
                <app-input
                    data-testid="cc-billing-zip-input"
                    class="cc-billing-zip-input ml-2"
                    label="Billing Zip"
                    v-model="billingZip"
                    :rules="'required|digits:5'"
                    :disabled="!recording"
                    hint="5 digits only"
                    :maxLength="5"
                ></app-input>
            </v-flex>
        </v-layout>
        <v-layout class="mb-4">
            <transition name="scroll-y-transition" mode="out-in">
                <v-btn
                    data-testid="cc-enter-btn"
                    class="cc-enter-btn warning"
                    small
                    block
                    v-if="recording"
                    @click="takeCC"
                    :loading="ccLoading"
                    key="cc-enter"
                >
                  Enter Credit Card
                </v-btn>
                <v-btn
                    data-testid="cc-save-btn"
                    class="cc-save-btn success"
                    small
                    block
                    v-else
                    @click="ccRetrieved"
                    :loading="ccLoading"
                    key="cc-save"
                >
                  Save Credit Card
                </v-btn>
            </transition>
        </v-layout>
        <v-layout>
            <v-flex xs6>
                <app-input
                    data-testid="cc-number-input"
                    class="cc-number-input mr-2"
                    autocomplete="off"
                    label="Credit Card Number"
                    v-model="ccNumber"
                    :prepend-icon="ccIcon"
                    type="creditCard"
                    :rules="{ credit_card: true}"
                    :disabled="recording"
                    @paste="updateCCType"
                ></app-input>
            </v-flex>
            <v-flex xs2>
                <v-select
                    data-testid="cc-exp-month-menu"
                    class="cc-exp-month-menu mx-2"
                    label="Expiration Month"
                    :items="availableCardMonths"
                    v-model="ccExpirationMonth"
                    :rules="[(v) => !!v || 'This field is required']"
                    :disabled="recording"
                />
            </v-flex>
            <v-flex xs2>
                <v-select
                    data-testid="cc-exp-year-menu"
                    class="cc-exp-year-menu mx-2"
                    label="Expiration Year"
                    :items="availableCardYears"
                    v-model="ccExpirationYear"
                    :rules="[(v) => !!v || 'This field is required']"
                    :disabled="recording"
                />
            </v-flex>
            <v-flex xs2>
                <app-input
                    data-testid="cc-cvv-code-input"
                    class="cc-cvv-code-input ml-2"
                    autocomplete="off"
                    label="Security/CVV Code"
                    v-model="ccCVV"
                    :rules="{ required: true, digits: cardIsAmex ? 4 : 3 }"
                    :disabled="recording"
                ></app-input>
            </v-flex>
        </v-layout>
    </v-form>
</template>

<script>
import { EventBus } from "../Event";
import { mapState } from "vuex";
import moment from "moment";
import { monthsList } from "@/helpers/months-list";
import states from "@/helpers/states";

export default {
  name: "CreditCard",
  data() {
    return {
      ccLoading: false,
      useExistingInfo: false,

      ccIcon: "credit_card",
      states: states,
      EventBus: EventBus,

      months: monthsList,
      years: this.ccYears(),

      pastedCCNumber: undefined
    };
  },
  methods: {
    fillForm() {
      let payload;
      if (this.creditCard.useExistingInfo) {
        payload = {
          firstName: this.info.first_name,
          lastName: this.info.last_name,
          billingAddress: this.info.street_address,
          billingState: this.info.state,
          billingZip: this.info.postal_code
        };
      } else {
        payload = {
          firstName: "",
          lastName: "",
          billingAddress: "",
          billingState: "",
          billingZip: ""
        };
      }
      this.$store.commit("autofillCreditCardInfo", payload);
    },

    takeCC() {
      // if twilio chat, bypass api call to stop ctm recording
      if (this.twilioChat) {
        this.$store.dispatch("handleSuccess", "Recording stopped. Enter the customer's credit card " +
                    "information, then press \"Save Credit Card\". You will not be able to perform any other " +
                    "actions until you have saved the card information.");
        this.$store.commit("recordingStopped");
      } else {
        this.ccLoading = true;
        this.$store.dispatch("stopRecording")
          .then(() => {
            this.$store.dispatch("handleSuccess", "Recording stopped. Enter the customer's credit card " +
                            "information, then press \"Save Credit Card\". You will not be able to perform any other " +
                            "actions until you have saved the card information.");
            this.ccLoading = false;
          })
          .catch(() => {
            this.$store.dispatch("handleError",
              `The system encountered an error while stopping the recording.
              Please wait a few seconds and try again.`);
            this.ccLoading = false;
          });
      }
    },

    ccRetrieved() {
      // if twilio chat, bypass api call to resume ctm recording
      if (this.twilioChat) {
        this.$store.dispatch("handleSuccess", "Recording resumed.");
        this.$store.commit("recordingStarted");
        this.$refs.ccInfoForm.validate();
        setTimeout(() => {
          if (this.$refs && this.$refs.ccInfoForm) {
            this.$refs.ccInfoForm.resetValidation();
          }
        }, 10000);
      } else {
        this.ccLoading = true;
        this.$store.dispatch("startRecording")
          .then(resp => {
            this.ccLoading = false;
            this.$store.dispatch("handleSuccess", "Recording resumed.");
            this.$refs.ccInfoForm.validate();
            setTimeout(() => {
              if (this.$refs && this.$refs.ccInfoForm) {
                this.$refs.ccInfoForm.resetValidation();
              }
            }, 10000);
          })
          .catch(() => {
            this.$store.dispatch("handleError",
              `The system encountered an error while resuming the recording.
              Please wait a few seconds and try again.`);
            this.ccLoading = false;
          });
      }
    },

    ccYears() {
      const currentYear = new Date().getFullYear();
      let years = [];

      years.push(currentYear);

      for (let i = 1; i < 11; i++) {
        years.push(currentYear + i);
      }

      return years;
    },

    updateCCType(event) {
      this.pastedCCNumber = event.clipboardData.getData("text");
      this.detectCCType(this.pastedCCNumber);
    },

    detectCCType(ccNumber) {
      let ccNum = ccNumber;
      let ccType;
      if (ccNum.length >= 1) {
        if (ccNum.charAt(0) === "5") {
          ccType = "master-card"; // mastercard
          this.ccIcon = "fab fa-cc-mastercard";
        } else if (ccNum.charAt(0) === "4") {
          ccType = "visa"; // visa
          this.ccIcon = "fab fa-cc-visa";
        } else if (ccNum.charAt(0) === "6") {
          ccType = "discover"; // discover
          this.ccIcon = "fab fa-cc-discover";
        } else if (ccNum.charAt(0) === "3") {
          ccType = "amex"; // american express
          this.ccIcon = "fab fa-cc-amex";
        } else {
          ccType = "";
          this.ccIcon = "credit_card";
        }
        this.$store.commit("setCCType", ccType);
      }
    }
  },
  computed: {
    ...mapState({
      creditCard: state => state.tenant.creditCard,
      info: state => state.tenant.info,
      recording: state => state.call.recording,
      twilioChat: state => state.application.twilioChat
    }),

    firstName: {
      get() {
        return this.creditCard.firstName;
      },
      set(newVal) {
        this.$store.commit("setCCFirstName", newVal);
      }
    },

    lastName: {
      get() {
        return this.creditCard.lastName;
      },
      set(newVal) {
        this.$store.commit("setCCLastName", newVal);
      }
    },

    billingAddress: {
      get() {
        return this.creditCard.billingAddress;
      },
      set(newVal) {
        this.$store.commit("setCCBillingAddress", newVal);
      }
    },

    billingState: {
      get() {
        return this.creditCard.billingState;
      },
      set(newVal) {
        this.$store.commit("setCCBillingState", newVal);
      }
    },

    billingZip: {
      get() {
        return this.creditCard.billingZip;
      },
      set(newVal) {
        this.$store.commit("setCCBillingZip", newVal);
      }
    },

    ccNumber: {
      get() {
        return this.creditCard.ccNumber;
      },
      set(newVal) {
        this.$store.commit("setCCNumber", newVal);
      }
    },

    ccExpirationMonth: {
      get() {
        return this.creditCard.ccExpirationMonth;
      },
      set(newVal) {
        this.$store.commit("setCCExpirationMonth", newVal);
      }
    },

    ccExpirationYear: {
      get() {
        return this.creditCard.ccExpirationYear;
      },
      set(newVal) {
        this.$store.commit("setCCExpirationYear", newVal);
      }
    },

    ccCVV: {
      get() {
        return this.creditCard.ccCVV;
      },
      set(newVal) {
        this.$store.commit("setCCCvv", newVal);
      }
    },

    cardIsAmex() {
      if (this.creditCard.ccNumber) {
        if (this.creditCard.ccNumber.substring(0, 2) === "34" || this.creditCard.ccNumber.substring(0, 2) === "37") {
          return true;
        }
      }
      return false;
    },

    cardMask() {
      // amex, 15 digits, regular cc number, 16 digits
      return this.cardIsAmex ? "#### ###### #####" : "#### #### #### ####";
    },

    cvvMask() {
      // amex, 4 digits, regular cc number, 3 digits
      return this.cardIsAmex ? "####" : "###";
    },

    validMonthsThisYear() {
      const monthIndex = moment().month();
      return this.months.slice(monthIndex, this.months.length);
    },

    availableCardMonths() {
      return (this.ccExpirationYear === this.years[0]) ? this.validMonthsThisYear : this.months;
    },

    availableCardYears() {
      const monthCheck = this.validMonthsThisYear.filter(month => this.ccExpirationMonth === month);
      return (this.ccExpirationMonth && !monthCheck.length) ? this.years.slice(1, this.years.length) : this.years;
    },

    toggleTypedOrPasted() {
      return !this.pastedCCNumber;
    }
  },
  watch: {
    ccNumber() {
      this.detectCCType(this.ccNumber);
    }
  },
  created() {
    EventBus.$on("ccCleared", () => {
      if (this.$refs && this.$refs.ccInfoForm) {
        this.$refs.ccInfoForm.resetValidation();
      }
    });
  }
};

</script>
